import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { apiBase } from './api';
import type { Session } from './auth';

export const HubEvents = {
  LeaderboardUpdated: 'LeaderboardUpdated',
  DrawingStaged: 'DrawingStaged',
  DrawingStarted: 'DrawingStarted',
  WinnerRevealed: 'WinnerRevealed',
  ReturnToLeaderboard: 'ReturnToLeaderboard',
  DisplayStatusChanged: 'DisplayStatusChanged',
  DrawingUpdated: 'DrawingUpdated',
} as const;

export type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

/**
 * Creates a self-healing hub connection. SignalR's automatic reconnect handles short drops; if it gives up,
 * we keep retrying forever (a shop TV must never need a human to press refresh). `onConnected` runs after the
 * first connect and after every reconnect so callers can resync authoritative state from the server.
 */
export function connectHub(opts: {
  session: Session;
  displayId?: string;
  onStatus?: (s: ConnectionStatus) => void;
  onConnected?: (hub: HubConnection) => void | Promise<void>;
}): { hub: HubConnection; stop: () => Promise<void> } {
  const url = `${apiBase}/hubs/rewards${opts.displayId ? `?displayId=${encodeURIComponent(opts.displayId)}` : ''}`;
  const hub = new HubConnectionBuilder()
    .withUrl(url, { accessTokenFactory: () => opts.session.getToken(), withCredentials: false })
    .withAutomaticReconnect({ nextRetryDelayInMilliseconds: (c) => Math.min(30_000, [0, 2_000, 5_000, 10_000][c.previousRetryCount] ?? 15_000) })
    .withServerTimeout(45_000)
    .withKeepAliveInterval(15_000)
    .configureLogging(LogLevel.Warning)
    .build();

  let stopped = false;
  let retry: ReturnType<typeof setTimeout> | undefined;

  const start = async () => {
    if (stopped) return;
    opts.onStatus?.('connecting');
    try {
      await hub.start();
      opts.onStatus?.('connected');
      await opts.onConnected?.(hub);
    } catch {
      opts.onStatus?.('disconnected');
      retry = setTimeout(start, 5_000);
    }
  };
  hub.onreconnecting(() => opts.onStatus?.('reconnecting'));
  hub.onreconnected(async () => { opts.onStatus?.('connected'); await opts.onConnected?.(hub); });
  hub.onclose(() => {
    opts.onStatus?.('disconnected');
    if (!stopped) retry = setTimeout(start, 5_000);
  });
  void start();

  return {
    hub,
    stop: async () => {
      stopped = true;
      if (retry) clearTimeout(retry);
      if (hub.state !== HubConnectionState.Disconnected) await hub.stop();
    },
  };
}
